import { useEffect, useState, useCallback } from "react";
import { api, imgSrc } from "@/lib/api";
import { useT } from "@/lib/i18n";
import Navbar from "@/components/layout/Navbar";
import Footer from "@/components/layout/Footer";
import { X, Play } from "lucide-react";

const CATEGORIES = ["cabins", "art", "grounds", "eatery", "experiences"];
const SPANS = [
    "md:col-span-7 aspect-[16/10]",
    "md:col-span-5 aspect-[4/3]",
    "md:col-span-5 aspect-[4/3]",
    "md:col-span-7 aspect-[16/10]",
    "md:col-span-6 aspect-[3/4]",
    "md:col-span-6 aspect-[3/4]",
];

export default function Gallery() {
    const { t } = useT();
    const [images, setImages] = useState([]);
    const [filter, setFilter] = useState("all");
    const [lightbox, setLightbox] = useState(null);

    const refresh = useCallback(async () => {
        const r = await api.get("/gallery", { params: filter === "all" ? {} : { category: filter } });
        setImages(r.data);
    }, [filter]);

    useEffect(() => { refresh(); }, [refresh]);

    return (
        <div data-testid="gallery-page" className="bg-bone min-h-screen">
            <Navbar />
            <section className="pt-32 lg:pt-40 pb-10 px-6 lg:px-12">
                <div className="max-w-[1500px] mx-auto">
                    <div className="eyebrow mb-4">{t("gallery.eyebrow")}</div>
                    <h1 className="font-chiller text-5xl lg:text-7xl leading-[1.02] tracking-tight max-w-3xl">
                        <span className="text-terracotta">A subtle atmosphere,</span><br /><span className="text-gold">inviting you</span> <span className="text-forest">to slow down</span>
                    </h1>

                    {/* Category filter */}
                    <div className="flex flex-wrap gap-2 mt-10" data-testid="gallery-filter">
                        {["all", ...CATEGORIES].map((c) => (
                            <button
                                key={c}
                                onClick={() => setFilter(c)}
                                data-testid={`gallery-filter-${c}`}
                                className={`text-xs uppercase tracking-widest2 px-5 py-3 border transition-colors ${filter === c ? "bg-forest text-bone border-forest" : "border-espresso/20 text-espresso hover:border-forest"}`}
                            >
                                {c === "all" ? t("gallery.cat.all") : t(`gallery.cat.${c}`)}
                            </button>
                        ))}
                    </div>
                </div>
            </section>

            <section className="px-6 lg:px-12 pb-24">
                <div className="max-w-[1500px] mx-auto">
                    {images.length === 0 ? (
                        <p className="text-sm text-muted2 italic" data-testid="gallery-empty">{t("gallery.empty")}</p>
                    ) : (
                        <div className="grid grid-cols-1 md:grid-cols-12 gap-4 md:gap-6">
                            {images.map((img, i) => (
                                <button
                                    key={img.id}
                                    onClick={() => setLightbox(img)}
                                    className={`group relative overflow-hidden bg-bone2 ${SPANS[i % SPANS.length]}`}
                                    data-testid={`gallery-img-${i}`}
                                >
                                    {img.media_type === "video" ? (
                                        <>
                                            <video src={imgSrc(img.url)} preload="metadata" muted playsInline className="w-full h-full object-cover transition-transform duration-1000 group-hover:scale-105" />
                                            <span className="absolute inset-0 flex items-center justify-center pointer-events-none">
                                                <span className="bg-espresso/50 group-hover:bg-terracotta/80 text-bone rounded-full p-4 transition-colors">
                                                    <Play size={24} fill="currentColor" />
                                                </span>
                                            </span>
                                        </>
                                    ) : (
                                        <img src={imgSrc(img.url)} alt={img.caption || `Gallery ${i + 1}`} className="w-full h-full object-cover transition-transform duration-1000 group-hover:scale-105" />
                                    )}
                                    {img.caption && (
                                        <span className="absolute bottom-0 left-0 right-0 bg-gradient-to-t from-espresso/80 to-transparent text-bone text-left px-4 py-3 text-sm font-serif opacity-0 group-hover:opacity-100 transition-opacity">
                                            {img.caption}
                                        </span>
                                    )}
                                </button>
                            ))}
                        </div>
                    )}
                </div>
            </section>

            {/* Lightbox */}
            {lightbox && (
                <div
                    className="fixed inset-0 z-[70] bg-espresso/95 flex items-center justify-center p-6"
                    onClick={() => setLightbox(null)}
                    data-testid="gallery-lightbox"
                >
                    <button onClick={() => setLightbox(null)} data-testid="gallery-lightbox-close" className="absolute top-6 right-6 text-bone hover:text-terracotta"><X size={28} /></button>
                    <figure className="max-w-5xl max-h-[85vh]" onClick={(e) => e.stopPropagation()}>
                        {lightbox.media_type === "video" ? (
                            <video src={imgSrc(lightbox.url)} controls autoPlay playsInline className="max-h-[80vh] w-auto mx-auto" data-testid="gallery-lightbox-video" />
                        ) : (
                            <img src={imgSrc(lightbox.url)} alt={lightbox.caption || "gallery"} className="max-h-[80vh] w-auto mx-auto object-contain" />
                        )}
                        {lightbox.caption && <figcaption className="text-center text-bone/80 font-serif text-lg mt-4">{lightbox.caption}</figcaption>}
                    </figure>
                </div>
            )}

            <Footer />
        </div>
    );
}
